// Library methods
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Components
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import TableCard from "src/components/shared/tables/TableCard/TableCard";
import EyeCompletionText from "src/components/shared/text/EyeCompletionText/EyeCompletionText";
import LinkDeviceTableCard from "src/components/containers/shared/LinkDeviceTableCard/LinkDeviceTableCard";
import {
  HeartbeatIcon,
  DownloadIcon,
  EditIcon,
  ViewFileIcon,
} from "src/assets/icons";

// Hooks
import useExamCardHelpers from "src/hooks/exams/useExamCardHelpers";
import useOnDemandReportGeneration from "src/hooks/reports/useOnDemandReportGeneration";

// Types
import { ExamType } from "src/services/exams/types";

// Styles
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Constants
import { DEFAULT_REPORT_TYPE } from "src/constants/enums";

// Utils
import { isMultiExamPackage } from "src/utils/exams/examReportsHelper";

type TodayExamCardType = {
  exam: ExamType;
  handleEditClick: (editExam: ExamType) => void;
};

const TodayExamCard = ({ exam, handleEditClick }: TodayExamCardType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const navigate = useNavigate();

  const theme = useTheme();
  const { elevatedSurface } = getSurfaceTokens(theme);
  const smallerThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  const { actions } = useLiveUpdateContext();
  const { handleViewLiveUpdateClick } = actions;

  const { downloadFile } = useOnDemandReportGeneration({
    patientId: exam?.patient?.id,
    examId: exam?.id,
  });

  const fileType = useMemo(
    () => clinicInfo?.settings?.defaultReportType ?? DEFAULT_REPORT_TYPE,
    [clinicInfo?.settings?.defaultReportType]
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const {
    deviceToUse,
    examToUse,
    examStatus,
    completedExamsText,
    examTypeText,
    examTooltipText,
    isLiveUpdateDisabled,
    isEditDisabled,
    isViewReportDisabled,
  } = useExamCardHelpers({ exam });

  const patientId = useMemo(
    () => examToUse?.patient?.id,
    [examToUse?.patient?.id]
  );

  const patientName = useMemo(
    () => `${examToUse?.patient?.firstName} ${examToUse?.patient?.lastName}`,
    [examToUse?.patient]
  );

  const displayLiveUpdateButton = useMemo(
    () =>
      examStatus?.status !== t("Completed") &&
      examStatus?.status !== t("Terminated") &&
      examStatus?.status !== t("Invalid"),
    [t, examStatus?.status]
  );

  // buttons are ordered in terms of importance / usage
  const examMenuButtons = useMemo(
    () => [
      {
        icon: <DownloadIcon />,
        tooltipText: t(`Download {{ fileType }} Report`, { fileType }),
        text: t(`Download {{ fileType }} Report`, { fileType }),
        onClick: async () => {
          setIsDownloading(true);
          await downloadFile();
          setIsDownloading(false);
        },
        display: !isViewReportDisabled,
        disabled: isDownloading,
        href: undefined,
      },
      {
        icon: <ViewFileIcon />,
        tooltipText: t("View Results"),
        text: t("View Report"),
        onClick: () => {},
        display: !isViewReportDisabled,
        href: `/patients/${patientId}/exam/${examToUse?.id}`,
      },
      {
        icon: <EditIcon />,
        text: t("Edit"),
        tooltipText: t("Edit Exam"),
        onClick: () => handleEditClick(examToUse),
        display: !isEditDisabled,
        href: undefined,
      },
    ],
    [
      t,
      examToUse,
      fileType,
      isDownloading,
      patientId,
      isEditDisabled,
      isViewReportDisabled,
      handleEditClick,
      downloadFile,
    ]
  );

  return (
    <Grid container item xs={12}>
      {/* Link device card */}
      <Grid item xs={1.5}>
        <LinkDeviceTableCard
          device={deviceToUse}
          exam={examToUse}
          examStatus={examStatus}
        />
      </Grid>

      <Grid item xs={10.5}>
        <TableCard
          variant="dark"
          hasHoverState={false}
          isSelectedForDeletion={false}
          isSelectedForViewing={false}
          onClick={() => {}}
          backgroundColorOverride={
            theme.palette.mode === "dark" ? elevatedSurface : undefined
          }
        >
          <Grid container item xs={12} alignItems="center">
            <Grid container item xs={10}>
              {/* Patient name */}
              <Grid
                item
                xs={12}
                sm={3}
                role="button"
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
                onClick={(e) => {
                  // open page in new tab if the meta key or ctrl key is pressed
                  if (e.metaKey || e.ctrlKey) {
                    localStorage.setItem("patient_id", examToUse?.patient?.id);
                    window.open("/patients", "_blank");
                    return;
                  }

                  navigate("/patients", {
                    state: { patient_id: examToUse?.patient?.id },
                  });
                }}
              >
                <Tooltip title={patientName}>
                  <Typography
                    sx={{
                      maxWidth: "fit-content",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      paddingRight: "16px",
                    }}
                  >
                    {smallerThanSm && (
                      <span style={{ fontWeight: 600 }}>{t("Patient")}: </span>
                    )}
                    {patientName}
                  </Typography>
                </Tooltip>
              </Grid>

              {/* Exam status */}
              <Grid item xs={12} sm={3}>
                <Tooltip
                  title={
                    <div style={{ whiteSpace: "pre-line" }}>
                      {isMultiExamPackage(examToUse)
                        ? completedExamsText
                        : examStatus?.status}
                    </div>
                  }
                >
                  <Typography
                    sx={{
                      maxWidth: "fit-content",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      paddingRight: "16px",
                    }}
                  >
                    {smallerThanSm && (
                      <span style={{ fontWeight: 600 }}>{t("Status")}: </span>
                    )}
                    {examStatus?.status}
                  </Typography>
                </Tooltip>
              </Grid>

              {/* Exam eye */}
              <Grid item xs={12} sm={3}>
                <Box
                  sx={{
                    paddingRight: "16px",
                    position: { xs: "relative", sm: "absolute" },
                    top: { xs: "0px", sm: "4px" },
                    width: "inherit",
                    maxWidth: "100%",
                    display: "block",
                  }}
                >
                  {smallerThanSm ? (
                    <div
                      style={{
                        display: "flex",
                        gap: "4px",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      {t("Eye")}: <EyeCompletionText exam={examToUse} />
                    </div>
                  ) : (
                    <EyeCompletionText exam={examToUse} />
                  )}
                </Box>
              </Grid>

              {/* Exam type */}
              <Grid item xs={12} sm={3}>
                <Tooltip
                  title={
                    <div style={{ whiteSpace: "pre-line" }}>
                      {examTooltipText}
                    </div>
                  }
                >
                  <Typography
                    sx={{
                      maxWidth: "fit-content",
                      display: "block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontSize: "14px",
                      paddingRight: "16px",
                    }}
                  >
                    {smallerThanSm && (
                      <span style={{ fontWeight: 600 }}>{t("Type")}: </span>
                    )}
                    {examTypeText}
                  </Typography>
                </Tooltip>
              </Grid>
            </Grid>

            {/* Action icons */}
            <Grid
              item
              xs={2}
              sx={{
                display: "flex",
                height: "100%",
                justifyContent: "flex-end",
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
              }}
            >
              <Tooltip title={t("Live Update Panel")}>
                <span>
                  <IconButton
                    disableRipple
                    disableFocusRipple
                    disableTouchRipple
                    aria-label="live-update-icon-button"
                    sx={{
                      py: { xs: "8px", sm: "0px" },
                      color: "inherit",
                      "&.Mui-disabled": {
                        color: "inherit",
                        opacity: "0.5",
                      },
                      display: displayLiveUpdateButton ? "block" : "none",
                      "@keyframes fade-in-out": {
                        "0%": {
                          opacity: "0.3",
                        },
                        "50%": {
                          opacity: "1",
                        },
                        "100%": {
                          opacity: "0.3",
                        },
                      },
                      animation: isLiveUpdateDisabled
                        ? "none"
                        : "fade-in-out 2s infinite",
                    }}
                    disabled={isLiveUpdateDisabled}
                    onClick={() => {
                      if (isLiveUpdateDisabled) return;
                      if (deviceToUse)
                        handleViewLiveUpdateClick(deviceToUse?.id);
                    }}
                  >
                    <HeartbeatIcon />
                  </IconButton>
                </span>
              </Tooltip>

              {examMenuButtons.map((examButton) => (
                <Tooltip
                  key={examButton.tooltipText}
                  title={examButton.tooltipText}
                >
                  <span>
                    <a
                      href={examButton.href}
                      style={{ textDecoration: "none", color: "inherit" }}
                      onClick={(e) => {
                        e.preventDefault();

                        // open report in new tab if the meta key or ctrl key is pressed
                        if (examButton.href && (e.metaKey || e.ctrlKey))
                          window.open(examButton.href, "_blank");
                        // else open the report in the same tab
                        else if (examButton.href) navigate(examButton.href);
                        else examButton.onClick();
                      }}
                    >
                      <IconButton
                        sx={{
                          py: { xs: "8px", sm: "0px" },
                          color: "inherit",
                          "&.Mui-disabled": {
                            color: "inherit",
                            opacity: "0.5",
                          },
                          display: !examButton.display ? "none" : "block",
                        }}
                        disabled={!examButton.display}
                      >
                        {examButton.icon}
                      </IconButton>
                    </a>
                  </span>
                </Tooltip>
              ))}
            </Grid>
          </Grid>
        </TableCard>
      </Grid>
    </Grid>
  );
};

export default TodayExamCard;
